import { useState, useEffect, useCallback, useRef } from 'react';

const LAST_READ_KEY   = 'quran_last_read';    // per-surah: { [chapterId]: {verse_key, verse_number, chapter_name, ts} }
const BOOKMARKS_KEY   = 'quran_bookmarks';    // array of {chapter_id, verse_key, chapter_name, verse_num, ts}
const ARABIC_ONLY_KEY = 'quran_arabic_only';
const VIEW_MODE_KEY   = 'quran_view_mode';    // 'cards' | 'mushaf'

/** Load all last-read positions (keyed by chapter_id) */
const loadAllLastRead = () => {
  try { return JSON.parse(localStorage.getItem(LAST_READ_KEY) || '{}'); }
  catch { return {}; }
};

/** Load bookmarks array */
const loadBookmarks = () => {
  try { return JSON.parse(localStorage.getItem(BOOKMARKS_KEY) || '[]'); }
  catch { return []; }
};

/**
 * useQuranProgress — manages:
 *  - per-surah last-read verse (auto-saved as user scrolls)
 *  - manual bookmarks
 *  - Arabic-only display preference
 *  - view mode (cards vs mushaf flow)
 */
export const useQuranProgress = () => {
  const [allLastRead, setAllLastRead] = useState(loadAllLastRead);
  const [bookmarks, setBookmarks] = useState(loadBookmarks);
  const [arabicOnly, setArabicOnly] = useState(() => {
    const saved = localStorage.getItem(ARABIC_ONLY_KEY);
    // Default to FALSE — show translation by default, Cards view is default
    return saved === null ? false : saved !== 'false';
  });
  const [viewMode, setViewMode] = useState(() => {
    // One-time migration: make Mushaf the main reading view for everyone.
    if (localStorage.getItem('quran_view_default_v3') !== '1') {
      localStorage.setItem('quran_view_default_v3', '1');
      localStorage.setItem(VIEW_MODE_KEY, 'mushaf');
      return 'mushaf';
    }
    return localStorage.getItem(VIEW_MODE_KEY) || 'mushaf';
  });

  /** Save last-read verse for a surah (only advances forward) */
  const saveLastRead = useCallback((chapterId, chapterName, verseKey, verseNumber) => {
    setAllLastRead(prev => {
      const existing = prev[chapterId];
      // Only advance — never go backwards (prevents mount-time overwrite)
      if (existing && existing.verse_number >= verseNumber) return prev;
      const updated = {
        ...prev,
        [chapterId]: { verse_key: verseKey, verse_number: verseNumber, chapter_name: chapterName, ts: Date.now() }
      };
      localStorage.setItem(LAST_READ_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  /** Get last-read for a specific chapter */
  const getLastRead = useCallback((chapterId) => allLastRead[chapterId] || null, [allLastRead]);

  /** Clear last-read for a specific chapter */
  const clearLastRead = useCallback((chapterId) => {
    setAllLastRead(prev => {
      const updated = { ...prev };
      delete updated[chapterId];
      localStorage.setItem(LAST_READ_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  /** Get the most recently read surah (for "Continue Reading" on home) */
  const getMostRecent = useCallback(() => {
    const entries = Object.entries(allLastRead);
    if (!entries.length) return null;
    return entries.sort((a, b) => b[1].ts - a[1].ts)[0];
  }, [allLastRead]);

  /** Toggle bookmark for a verse */
  const toggleBookmark = useCallback((chapterId, chapterName, verseKey, verseNumber) => {
    setBookmarks(prev => {
      const exists = prev.findIndex(b => b.verse_key === verseKey && b.chapter_id === chapterId);
      let updated;
      if (exists >= 0) {
        updated = prev.filter((_, i) => i !== exists);
      } else {
        updated = [...prev, { chapter_id: chapterId, chapter_name: chapterName, verse_key: verseKey, verse_number: verseNumber, ts: Date.now() }];
      }
      localStorage.setItem(BOOKMARKS_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const isBookmarked = useCallback((verseKey) => bookmarks.some(b => b.verse_key === verseKey), [bookmarks]);

  const toggleArabicOnly = useCallback(() => {
    setArabicOnly(prev => {
      localStorage.setItem(ARABIC_ONLY_KEY, String(!prev));
      return !prev;
    });
  }, []);

  const toggleViewMode = useCallback(() => {
    setViewMode(prev => {
      const next = prev === 'cards' ? 'mushaf' : 'cards';
      localStorage.setItem(VIEW_MODE_KEY, next);
      return next;
    });
  }, []);

  return {
    allLastRead, getLastRead, saveLastRead, clearLastRead, getMostRecent,
    bookmarks, toggleBookmark, isBookmarked,
    arabicOnly, toggleArabicOnly,
    viewMode, toggleViewMode,
  };
};

/**
 * useScrollTracker — uses IntersectionObserver to track which verse is visible.
 * Calls onVerseVisible(verseKey, verseNumber) when a verse enters the viewport.
 */
export const useScrollTracker = (verses, onVerseVisible) => {
  const refs = useRef({});
  const observerRef = useRef(null);

  useEffect(() => {
    if (!verses?.length || !onVerseVisible) return;

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const key = entry.target.dataset.verseKey;
            const num = parseInt(entry.target.dataset.verseNumber, 10);
            if (key) onVerseVisible(key, num);
          }
        });
      },
      { threshold: 0.4, rootMargin: '0px 0px -30% 0px' }
    );

    Object.values(refs.current).forEach(el => {
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [verses, onVerseVisible]);

  /** Call this as a ref callback: ref={el => setRef(verse.verse_key, el)} */
  const setRef = useCallback((key, el) => {
    if (el) refs.current[key] = el;
  }, []);

  return { setRef };
};
